const CART_MESSAGES = {
  select: () => 'Select Size',
  notify: (size) => `Notify me when ${size} is back`,
  out_of_stock: () => 'Out of Stock'
}

const MESSAGE_TYPES = {
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR'
}
/**
 * Product form
 */
class ProductForm {
  static PRODUCT_DETAILS_SELECTOR = '[data-role="product_id"]'
  static FORM_SELECTOR = '#product-form'
  static FORM_SUBMIT_SELECTOR = '[data-form="product-form"]'
  static FORM_SUBMIT_MESSAGE_SELECTOR = 'span:first-child'
  static NOTIFY_SELECTOR = '[data-role="klaviyo_notify_me"]'
  static PRODUCT_SELECT_SELECTOR = 'x-product-selector'
  static NOTIFY_INPUT_SELECTOR = '[data-role="klaviyo_input"]'
  static NOTIFY_SUCCESS_MESSAGE_SELECTOR = '#BackInStockSuccess'
  static NOTIFY_ERROR_MESSAGE_SELECTOR = '#BackInStockError'
  static WISHLIST_BUTTON_SELECTOR = 'x-storage'
  static IS_GIFT_CARD_SELECTOR = 'input[name="pdp_type"]'
  static BAG_PRICE_DISPLAY = '[data-role="bag_price_display"]'
  static GIFT_CARD_ERROR_SELECTOR = '#gc-error'

  constructor() {
    this.storage = window.wishlistStorage /** wishlist local storage  */
    this.apiRevision = '2024-06-15'

    this.formDataKeys = {
      ID: 'id',
      QUANTITY: 'quantity',
      UNAVAILABLE_STOCK: 'unavailable_stock',
      BAG_PRICE_DISPLAY: 'gift-card-total',
      FROM: 'From',
      TO: 'To',
      EMAIL: 'Email',
      MESSAGE: 'Message'
    }

    this.messageClass = {
      [MESSAGE_TYPES.SUCCESS]: '',
      [MESSAGE_TYPES.ERROR]: ''
    }
    this.notifiedVariants = new Set()
    this.productId = undefined
    this.selectedVariant = undefined
    this.isGiftCardProduct = false
    this.init()
  }

  /**
   * init function fetches all the elements that the Product form are dependent of
   * and applies their eventlistener events.
   */
  init() {
    this.fetchChildren()
    if (!this.productId) return
    this.addEventListeners()
  }

  fetchChildren() {
    /** PRODUCT DETAILS */
    const productDetails = document.querySelector(ProductForm.PRODUCT_DETAILS_SELECTOR)
    this.productId = productDetails?.value || productDetails?.innerText

    /**
     * If the productId attribute is still null after the above
     * Do not progress any further
     */
    if (!this.productId) return

    /** FORM ATTRIBUTES */
    this.form = document.querySelector(ProductForm.FORM_SELECTOR)
    this.formSubmitButton = document.querySelector(ProductForm.FORM_SUBMIT_SELECTOR)
    this.productSelector = document.querySelector(ProductForm.PRODUCT_SELECT_SELECTOR)

    /** FORM SUBMIT BUTTON UNSELECTED PRODUCT */
    this.formSubmitButtonMessage = this.formSubmitButton.querySelector(ProductForm.FORM_SUBMIT_MESSAGE_SELECTOR)

    /** KLAVIYO ATTRIBUTES */
    this.notifyForm = document.querySelector(ProductForm.NOTIFY_SELECTOR)
    this.notifyInputForm = document.querySelector(ProductForm.NOTIFY_INPUT_SELECTOR)

    this.notifySuccessElement = document.querySelector(ProductForm.NOTIFY_SUCCESS_MESSAGE_SELECTOR)
    this.notifyErrorElement = document.querySelector(ProductForm.NOTIFY_ERROR_MESSAGE_SELECTOR)
    /** WISHLIST SELECTOR */
    this.wishlistButton = document.querySelector(ProductForm.WISHLIST_BUTTON_SELECTOR)
    this.checkProductType()
  }

  checkProductType() {
    const giftCard = document.querySelector(ProductForm.IS_GIFT_CARD_SELECTOR)
    if (!giftCard) return
    this.isGiftCardProduct = true
    this.bagPriceDisplay = document.querySelector(ProductForm.BAG_PRICE_DISPLAY)
  }

  addEventListeners() {
    this.form.addEventListener('click', this.handleEnableAddToCartCallback.bind(this))
    this.formSubmitButton.addEventListener('click', this.handleAddToCartCallback.bind(this))
  }

  setSelectedVariant(variant) {
    this.selectedVariant = variant
  }

  getSelectedVariant() {
    return this.selectedVariant
  }

  /**
   *
   * @param {*} formData FormData type
   */
  pullFormData(formData) {
    const keys = Object.values(this.formDataKeys)
    const payload = keys.reduce((acc, key) => ({ ...acc, [key]: formData.get(key) ?? '' }), {})
    this.setSelectedVariant(payload[this.formDataKeys.ID])
    return payload
  }

  /**
   *
   * @param {*} event onClick EventListener
   */
  handleEnableAddToCartCallback(event) {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    const element = this.formSubmitButton
    const data = this.pullFormData(formData)

    /** Data attributes */
    const variantSelected = data[this.formDataKeys.ID]
    const unavailableStock = data[this.formDataKeys.UNAVAILABLE_STOCK]

    this.handleWishList()
    this.resetElements()
    /** Check to see if an item is marked with unavailable stock */
    if (unavailableStock) {
      element.setAttribute('data-ui', '')

      const notificationType = this.formSubmitButtonMessage?.dataset?.notificationType
      if (notificationType === 'sold_out') {
        this.formSubmitButtonMessage.innerText = CART_MESSAGES.out_of_stock()
        if (this.notifyForm) this.notifyForm.dataset.ui = ''
        this.toggleAddToCartDisabled(true)
        return
      }

      this.formSubmitButtonMessage.innerText = CART_MESSAGES.notify(unavailableStock)
      if (this.notifyForm) this.notifyForm.dataset.ui = 'visible'
      this.handleVariantChange(this.getSelectedVariant())
      return
    }

    if (!variantSelected) {
      return
    }

    if (this.notifyForm) this.notifyForm.dataset.ui = ''
    this.formSubmitButtonMessage.innerText = CART_MESSAGES.select()
    element.setAttribute('data-ui', 'visible')

    if (this.isGiftCardProduct === true) {
      this.bagPriceDisplay.innerText = data[this.formDataKeys.BAG_PRICE_DISPLAY]
    }
  }

  validateGiftCardForm(data) {
    if (!data[this.formDataKeys.FROM]) {
      this.displayErrorMessage('Sender Name is required', ProductForm.GIFT_CARD_ERROR_SELECTOR)
      return
    }
    if (!data[this.formDataKeys.TO]) {
      this.displayErrorMessage('Recipient Name is required', ProductForm.GIFT_CARD_ERROR_SELECTOR)
      return
    }
    if (!data[this.formDataKeys.EMAIL]) {
      this.displayErrorMessage('Recipient Email is required', ProductForm.GIFT_CARD_ERROR_SELECTOR)
      return
    }

    if (!this.validateEmail(data[this.formDataKeys.EMAIL])) {
      this.displayErrorMessage('Recipient Email is invalid', ProductForm.GIFT_CARD_ERROR_SELECTOR)
      return
    }
    return true
  }

  itemPayload(data) {
    const id = data[this.formDataKeys.ID]
    const quantity = data[this.formDataKeys.QUANTITY]
    const item = { id, quantity }

    if (this.isGiftCardProduct === true) {
      const itemWithProperties = {
        ...item,
        properties: {
          From: data[this.formDataKeys.FROM],
          To: data[this.formDataKeys.TO],
          Email: data[this.formDataKeys.EMAIL],
          Message: data[this.formDataKeys.MESSAGE]
        }
      }

      return itemWithProperties
    }
    return item
  }

  /**
   *
   * @param {*} event onClick EventListener
   */
  async handleAddToCartCallback(event) {
    event.preventDefault()
    event.stopPropagation()
    const ele = event.currentTarget
    const formData = new FormData(this.form)
    const data = this.pullFormData(formData)
    const unavailableStock = data[this.formDataKeys.UNAVAILABLE_STOCK]

    /** If no values have been selected scroll the product variant selector into view */
    if (!ele.dataset?.ui && !unavailableStock) {
      this.productSelector.scrollIntoView()
      return
    }

    if (this.isGiftCardProduct === true && !this.validateGiftCardForm(data)) {
      return
    }

    if (unavailableStock) {
      const notificationType = this.formSubmitButtonMessage?.dataset?.notificationType
      if (notificationType === 'sold_out') {
        return
      }
      const customerEmail = this.notifyInputForm?.value
      const klaviyoCompanyId = this.notifyInputForm.dataset.klaviyoCompanyId
      const unavailableStockVariant = this.getSelectedVariant()
      await this.klaviyoBackInStockNotifications({
        customerEmail,
        klaviyoCompanyId,
        unavailableStockVariant
      })
      return
    }

    const item = this.itemPayload(data)
    window.Cart.addLineItems(item)
  }

  /**
   * Because we need to check if the item selected is in the wishlist by product variant
   * we will need to trigger a event to check the wishlistStorage whenever the Form onChange
   * is triggered.
   */
  handleWishList() {
    if (GM_STATE.customer.logged || !this.wishlistButton) return
    const variant = this.getSelectedVariant()
    const item = `${this.productId}:${variant}`
    this.wishlistButton.dataset.product = item
    this.wishlistButton.reRun()
  }

  /** Klaviyo Back in stock notifications */
  async klaviyoBackInStockNotifications({ customerEmail, klaviyoCompanyId, unavailableStockVariant }) {
    this.resetElements()
    if (!customerEmail.length) {
      this.displayErrorMessage('Email Address can not be blank.')
      return
    }
    const emailIsValid = this.validateEmail(customerEmail)
    if (!emailIsValid) {
      this.displayErrorMessage('Please enter in a valid email.')
      return
    }

    try {
      const response = await this.handleSubscription({ customerEmail, klaviyoCompanyId, unavailableStockVariant })
      await this.handleResponse(response, unavailableStockVariant)
    } catch (err) {
      console.error(err)
    }
  }

  handleVariantChange(id) {
    this.notifiedVariants.has(id) ? this.displaySuccessMessage() : this.resetElements()
  }

  validateEmail(email) {
    return email.match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    )
  }

  /**
   *

   *
   * @param {*} param0
   * @returns
   */
  async handleSubscription({ customerEmail, klaviyoCompanyId, unavailableStockVariant }) {
    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email: customerEmail }
            }
          },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${unavailableStockVariant}`
            }
          }
        }
      }
    }

    return fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${klaviyoCompanyId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: this.apiRevision
      },
      body: JSON.stringify(payload)
    })
  }

  async handleResponse(response, variantId) {
    if (response.ok) {
      this.notifiedVariants.add(variantId)
      this.displaySuccessMessage()
    } else {
      await this.handleSubscriptionError(response)
    }
  }

  async handleSubscriptionError(response) {
    try {
      const { errors } = await response.json()
      const errorMessages = {
        variant_not_found: 'Error submitting form, please try again or contact support',
        invalid: 'Invalid email'
      }

      this.displayErrorMessage(errorMessages[errors[0].code] || errorMessages.variant_not_found)
    } catch (error) {
      console.error('Error parsing response:', error)
      this.displayErrorMessage('Error submitting form, please try again or contact support')
    }
  }

  toggleAddToCartDisabled(isDisabled) {
    if (isDisabled) {
      this.formSubmitButton.setAttribute('disabled', '')
      this.formSubmitButton.classList.add('opacity-70')
      return
    }
    this.formSubmitButton.removeAttribute('disabled', '')
    this.formSubmitButton.classList.remove('opacity-70')
  }

  applyNoStockAvailableCTA() {
    this.formSubmitButtonMessage.innerText = CART_MESSAGES.out_of_stock()
  }

  displaySuccessMessage() {
    this.notifyInputForm.classList.add('hidden')
    this.applyNoStockAvailableCTA()
    this.toggleAddToCartDisabled(true)
    this.notifySuccessElement?.classList.remove('-mt-7')
    this.notifySuccessElement?.classList.remove('opacity-0')
    this.notifySuccessElement?.classList.remove('max-h-0')
    this.notifySuccessElement?.classList.add('pt-2')
  }

  displayErrorMessage(message, elementIDOverride = false) {
    if (elementIDOverride) {
      this.notifyErrorElement = document.querySelector(elementIDOverride)
    }
    if (this.notifyErrorElement) {
      this.notifySuccessElement?.classList.remove('-mt-7')
      this.notifyErrorElement.textContent = message
      this.notifyErrorElement.classList.remove('max-h-0')
    }
  }

  resetElements() {
    if (this.notifyInputForm) this.notifyInputForm.classList.remove('hidden')
    this.toggleAddToCartDisabled(false)
    if (this.notifySuccessElement) {
      this.notifySuccessElement?.classList.add('opacity-0')
      this.notifySuccessElement?.classList.add('-mt-7')
    }
    if (this.notifyErrorElement) {
      this.notifyErrorElement?.classList.add('max-h-0')
    }
  }
}

const productForm = new ProductForm()
